<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      v-if="currentUser(['ROLE_TENANT'])"
      app
    >
      <div class="mx-auto my-4 text-center">
        <v-img
          alt="Logo"
          class="shrink mx-auto mb-2 click_cursor"
          contain
          :src="imageSource(get('config').logo, '', 75)"
          transition="scale-transition"
          height="75px"
        />

        <div
          v-if="!mini"
          class="text-h6 font-weight-bold click_cursor"
        >
          {{ site_name }}
        </div>
      </div>

      <v-list
        nav
        active-class
      >
        <!-- Dashboard Link (einzeln) -->
        <v-list-item link>
          <v-list-item-icon>
            <v-icon :class="checkProduct(menuItems[0].product) ? '' : 'secondary--text'
              ">{{ menuItems[0].icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content @click="linkTo(menuItems[0].url)">
            <v-list-item-title :class="checkProduct(menuItems[0].product) ? '' : 'secondary--text'">{{ getNavText(menuItems[0]) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Upload Links (beide) -->
        <v-list-group
          v-model="uploadSelected"
          :value="true"
          prepend-icon="mdi-upload"
        >
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>{{ $t('nav.sidebar.upload') }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            class="px-4"
            v-for="(item, i) in uploadMenuItems"
            :key="i"
            link
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content @click="linkTo(item.url)">
              <v-list-item-title :class="checkProduct(item.product) ? '' : 'secondary--text'">{{ getNavText(item) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <!-- Content Links (alle)-->
        <v-list-group
          v-model="contentSelected"
          :value="true"
          prepend-icon="mdi-table-of-contents"
        >
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>{{ $t('nav.sidebar.content') }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            class="px-4"
            v-for="(item, i) in contentMenuItems"
            :key="i"
            link
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content @click="linkTo(item.url)">
              <v-list-item-title :class="checkProduct(item.product) ? '' : 'secondary--text'">{{ getNavText(item) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <!-- Normale Links (alle)-->
        <v-list-item
          v-for="(item, i) in menuItems.slice(1)"
          :key="i"
          link
          exact-path
        >
          <v-list-item-icon v-if="item.icon != 'mdi-upload'">
            <v-icon :class="checkProduct(item.product) ? '' : 'secondary--text'">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content @click="linkTo(item.url)">
            <v-list-item-title :class="checkProduct(item.product) ? '' : 'secondary--text'">{{ getNavText(item) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <!-- Ende Normale Links -->
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      v-if="currentUser(['ROLE_TENANT', 'ROLE_USER', 'ROLE_END_USER'])"
      app
      flat
    >
      <div
        class="pr-4"
        v-if="currentUser(['ROLE_TENANT'])"
      >
        <v-icon
          @click="drawer = !drawer"
          color="#00C3FF"
        >mdi-menu</v-icon>
      </div>

      <!-- Menü für Consumer -->
      <div
        v-if="
          currentUser(['ROLE_USER', 'ROLE_END_USER']) ||
          (currentUser(['ROLE_TENANT']) &&
            [
              '/',
              '/home',
              '/category',
              '/playlists',
              '/galleries',
              '/images',
            ].includes($route.path))
        "
        class="d-flex align-center click_cursor"
      >
        <v-img
          alt="Logo"
          class="shrink mr-2 ml-1 click_cursor"
          contain
          :src="imageSource(get('config').logo)"
          transition="scale-transition"
          width="32"
        />
        <h2
          @click="linkTo('home')"
          class="ml-2 mr-6 line-height-1"
        >
          {{ site_name }}
        </h2>
        <div
          v-for="(link, index) in consumerLinks"
          :key="index"
          class="hidden-md-and-down"
        >
          <span
            text
            class="mr-6"
            :class="{
              'font-weight-bold': isActiveLink(link.url),
              'primary--text': isActiveLink(link.url),
            }"
            v-if="checkProduct(link.product)"
            @click="linkTo(link.url)"
          >
            {{ getNavText(link) }}
          </span>
        </div>
      </div>

      <v-row class="hidden-md-and-down ma-0">
        <v-col
          cols="3"
          class="mr-3"
        >
          <search />
        </v-col>
      </v-row>

      <v-spacer></v-spacer>

      <div
        v-if="get('cart')"
        ref="cartIcon"
        @click="openCartDrawer(cartDrawerState)"
        class="mr-6 click_cursor"
        :class="{ 'cart-shake': cartShaking }"
        @animationend="cartShaking = false"
      >
        <v-badge
          :content="get('cart').length"
          :value="get('cart').length"
          overlap
        >
          <v-icon
            color="#00C3FF"
            size="28px"
          >mdi-cart-outline</v-icon>
        </v-badge>
      </div>

      <div v-if="currentUser(['ROLE_TENANT'])">
        <v-btn
          v-if="isTester() && !isCreatorSubActive()"
          rounded
          small
          class="mr-4 animated-gradient-dBlue-red-radial hidden-xs-only"
          @click="linkTo('checkout')"
        >
          {{ $t('nav.sidebar.upgrade_plan') }}
        </v-btn>
      </div>
      <!--Ein Trigger unter 1265 px. Das Menue selbst liegt im rechten Drawer.
          Der Close-Button im Header war unerreichbar: der Drawer-Overlay liegt 
          ueber der App-Bar (z-index 7 gegen 5). -->
      <div
        class="hidden-lg-and-up"
        v-if="currentUser(['ROLE_END_USER'])"
      >
        <v-icon @click="openMobileMenu()">mdi-menu</v-icon>
      </div>

      <!--Consumer unter 1265 px nutzen den Drawer als einziges Menue.
          Tenant und User behalten das Avatar-Menue auf jeder Breite. -->
      <div :class="{ 'hidden-md-and-down': currentUser(['ROLE_END_USER']) }">
        <v-menu
          open-on-click
          offset-x-bottom
          v-if="currentUser(['ROLE_TENANT', 'ROLE_USER', 'ROLE_END_USER'])"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="pr-4 d-flex"
              v-bind="attrs"
              v-on="on"
            >
              <v-avatar
                class="mr-1 click_cursor"
                width="32"
                height="32"
                outline
              >
                <img
                  :src="imageSource(get('user').image)"
                  alt="avatar"
                  width="32"
                />
                <!-- <img :src="userImage" alt="avatar" width="32"> -->
              </v-avatar>
            </div>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-icon>
                <v-icon>mdi-theme-light-dark</v-icon>
              </v-list-item-icon>
              <v-switch
                @click="toogleDarkTheme"
                hide-details
                inset
                dense
                class="mt-1"
              >
              </v-switch>
            </v-list-item>

            <v-list-item>
              <v-list-item-icon>
                <v-icon>mdi-web</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <language-switcher />
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="currentUser(['ROLE_END_USER', 'ROLE_USER'])"
              @click="linkTo('userprofil')"
            >
              <v-list-item-icon>
                <v-icon>mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <span class="pl-n4">{{ $t('nav.sidebar.profile') }}</span>
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-else
              @click="linkTo('admin_profil')"
            >
              <v-list-item-icon>
                <v-icon>mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <span class="pl-n4">{{ $t('nav.sidebar.profile_settings') }}</span>
              </v-list-item-title>
            </v-list-item>

            <v-divider></v-divider>
            <v-list-item
              v-on:submit.prevent
              @click="logout()"
              link
            >
              <v-list-item-icon>
                <v-icon>mdi-logout-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <span class="pl-n4">{{ $t('common.logout') }}</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-app-bar>

    <v-navigation-drawer
      v-if="currentUser(['ROLE_END_USER'])"
      v-model="mobileDrawer"
      temporary
      right
      fixed
      width="179"
    >
      <div class="d-flex flex-column fill-height">
        <v-list
          nav
          dense
        >
          <v-list-item>
            <v-list-item-content>
              <div class="d-flex justify-space-between">
                <v-list-item-title class="primary--text headline font-weight-medium my-2">{{ $t('nav.sidebar.menu') }}</v-list-item-title>
                <v-icon
                  class="click_cursor"
                  size="21"
                  color="primary"
                  @click="mobileDrawer = !mobileDrawer"
                >mdi-close</v-icon>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Menuepunkte unten: bessere Daumenreichweite. Logout oben, Profil unten. -->
        <v-list
          nav
          dense
          class="mt-auto"
        >
          <v-list-item
            v-on:submit.prevent
            @click="logout()"
            link
          >
            <v-list-item-icon>
              <v-icon>mdi-logout-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t('common.logout') }}
            </v-list-item-title>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item class="d-flex">
            <v-list-item-icon>
              <v-icon>mdi-theme-light-dark</v-icon>
            </v-list-item-icon>
            <v-switch
              @click="toogleDarkTheme"
              hide-details
              inset
              dense
              class="mt-1"
            />
          </v-list-item>

          <v-list-item class="d-flex">
            <v-list-item-icon>
              <v-icon>mdi-web</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <language-switcher />
            </v-list-item-content>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item
            v-for="(link, index) in consumerLinks"
            :key="index"
            @click="linkTo(link.url)"
            link
          >
            <v-list-item-icon v-if="link.icon">
              <v-icon>{{ link.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title v-if="checkProduct(link.product)">{{
              getNavText(link)
            }}</v-list-item-title>
          </v-list-item>

          <v-divider></v-divider>

          <!-- Profil: ersetzt das Avatar-Menue unter 1265 px -->
          <v-list-item
            @click="linkTo('userprofil')"
            link
          >
            <v-list-item-icon>
              <v-avatar
                size="24"
                class="ml-1"
              >
                <img
                  v-if="userImage && !avatarError"
                  :src="imageSource(userImage)"
                  :alt="$t('nav.sidebar.profile')"
                  @error="avatarError = true"
                />
                <v-icon v-else>mdi-account</v-icon>
              </v-avatar>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('nav.sidebar.profile') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </v-navigation-drawer>

    <response />

    <rightSiteMenu />

    <v-snackbar
      v-model="cookieDialog"
      origin="bottom right"
      right
      bottom
      max-width="100px"
      timeout="-1"
    >
      <div class="">
        <div class="d-flex align-center">
          <v-icon
            class="headline primary--text mb-4"
            color="primary"
            size="32"
          >mdi-cookie</v-icon>
          <p class="headline ml-2">{{ $t('cookies.title') }}</p>
        </div>
        <p class="pr-4">
          {{ $t('cookies.text') }}
          <span
            class="click_cursor primary--text"
            @click="linkTo('Datenschutzerklaerung')"
          >{{ $t('cookies.link') }}</span>
        </p>
        <div class="d-flex justify-end">
          <v-btn
            text
            color="green"
            @click="setCookie"
          > {{ $t('common.ok') }} </v-btn>
        </div>
      </div>
    </v-snackbar>

    <v-theme-provider>
      <v-main>
        <v-scroll-x-transition>
          <router-view />
        </v-scroll-x-transition>
      </v-main>
    </v-theme-provider>

    <v-footer
      v-if="currentUser(['ROLE_TENANT', 'ROLE_END_USER'])"
      width="100%"
    >
      <div class="app-footer">
        <div class="app-footer-links grey--text">
          <span
            class="caption click_cursor"
            @click="linkTo('Datenschutzerklaerung')"
          >{{ $t('nav.footer.privacy') }}</span>
          <i class="app-footer-sep">∙</i>
          <span
            class="caption click_cursor"
            @click="linkTo('Nutzungsbedingungen')"
          >{{ $t('nav.footer.terms') }}</span>
          <i class="app-footer-sep">∙</i>
          <span
            class="caption click_cursor"
            @click="linkTo('content-removal')"
          >{{ $t('nav.footer.report_content') }}</span>
          <i class="app-footer-sep">∙</i>
          <span
            class="caption click_cursor"
            @click="linkTo('depicture-removal')"
          >{{ $t('nav.footer.report_image') }}</span>
          <i class="app-footer-sep">∙</i>
          <span
            class="caption click_cursor"
            @click="linkTo('Impressum')"
          >{{ $t('nav.footer.imprint') }}</span>
          <i class="app-footer-sep">∙</i>
          <span
            class="caption click_cursor"
            @click="openModal('feedbackForm')"
          >{{ $t('nav.footer.support') }}</span>
        </div>

        <div class="app-footer-copy grey--text">
          <span class="caption">
            © {{ new Date().getFullYear() }} {{ $t('nav.footer.powered_by') }}
            <a
              href="https://liox.io"
              target="_blank"
              class="font-weight-bold"
            >
              LIOX.io
            </a>
          </span>
          <i class="app-footer-sep">∙</i>
          <span class="caption">{{ $t('nav.footer.rights') }}</span>
        </div>
      </div>
    </v-footer>

    <component :is="isSocialSharing" />

    <feedbackForm />
  </v-app>
</template>

<script>
import { mapGetters } from "vuex";
import userRole from "@/services/userRoleService";
import APIService from "@/services/api.service";
import AppService from "@/services/app.service";
import PaymentService from "@/services/payment.service";
import LanguageSwitcher from "@/components/languageSwitcher.vue";

const socialSharing = () => import("@/components/modal/socialSharing");
const rightSiteMenu = () => import("@/components/rightSiteMenu/rightSiteMenu");


export default {
  name: "App",
  components: {
    response: () => import("@/components/response/response"),
    search: () => import("@/components/search/globalSearch"),
    feedbackForm: () => import("@/components/modal/feedbackForm"),
    LanguageSwitcher
  },

  metaInfo() {
    return {
      // title: this.dynamicTitle,
      meta: [
        { name: "title", content: this.dynamicTitle },
        { name: "description", content: this.dynamicDescription },
        ...(AppService.isExplicit()
          ? [
            { name: "rating", content: "18" },
            { name: "rating", content: "adult" },
            { name: "rating", content: "FSK 18" },
            { name: "rating", content: "RTA-5042-1996-1400-1577-RTA" },
          ]
          : [{ name: "rating", content: "0" }]),
      ],
    };
  },
  data() {
    return {
      title: "Mediathek",
      dynamicTitle: "",
      dynamicDescription: "powered by LIOX.io",
      loading: false,
      val: Boolean,
      cartDrawerState: true,
      cartShaking: false,
      normSelected: 0,
      uploadSelected: 0,
      contentSelected: 0,
      mobileDrawer: false,
      avatarError: false,
      drawer: true,
      cookieDialog: false,
      mini: false,
      isLoading: false,
      items: [],
      model: null,
      remainTester: true,
      search: null,
      admins: [
        ["Management", "mdi-account-multiple-outline"],
        ["Settings", "mdi-cog-outline"],
      ],
      cruds: [
        ["Create", "mdi-plus-outline"],
        ["Read", "mdi-file-outline"],
        ["Update", "mdi-update"],
        ["Delete", "mdi-delete"],
      ],
      uploadMenuItems: [
        {
          text: "nav.sidebar.video_upload",
          url: "video-upload",
          icon: "mdi-video-plus",
          product: "Starter",
        },
        {
          text: "nav.sidebar.image_upload",
          url: "image-upload",
          icon: "mdi-image-plus",
          product: "Starter",
        },
      ],
      contentMenuItems: [
        {
          text: "nav.sidebar.videos",
          icon: "mdi-video-account",
          url: "videos",
          product: "Starter",
        },
        {
          text: "nav.sidebar.playlists",
          icon: "mdi-folder-play-outline",
          url: "playlisten",
          product: "Starter",
        },
        {
          text: "nav.sidebar.categories",
          icon: "mdi-view-list",
          url: "categories",
          product: "Starter",
        },
      ],
      menuItems: [
        {
          text: "nav.sidebar.dashboard",
          icon: "mdi-view-dashboard-variant",
          url: "admin",
          product: "Starter",
        },
        {
          text: "nav.sidebar.my_site",
          icon: "mdi-theater",
          url: "home",
          product: "Starter",
        },
        {
          text: "nav.sidebar.stats",
          icon: "mdi-finance",
          url: "stats",
          product: "Starter",
        },
        {
          text: "nav.sidebar.users",
          icon: "mdi-account-group",
          url: "user",
          product: "Starter",
        },

        {
          text: "nav.sidebar.settings",
          icon: "mdi-application-cog-outline",
          url: "einstellungen",
          product: "Starter",
        },
        {
          text: "nav.sidebar.landingpage",
          icon: "mdi-airplane-landing",
          url: "/",
          product: "Starter",
        },
      ],
      consumerLinks: [
        { text: "nav.sidebar.videos", icon: "mdi-view-list", url: "category", product: "Starter" },
      ],
    };
  },

  computed: {
    ...mapGetters(["get"]),

    // Profilbild fuer den Drawer. Der Getter liefert [] bzw. eine leere Zeichenkette,
    // solange /users/me nicht geantwortet hat. Dann greift das Icon als Ersatz.
    userImage: {
      get() {
        const user = this.get("user");
        const image = user && user.image;
        return typeof image === "string" && image.trim() !== "" ? image : null;
      },
    },

    site_name: {
      get() {
        if (typeof this.$store.state.main.config.result !== "undefined")
          return this.$store.state.main.config.result.site_name;
      },
    },

    isSocialSharing: {
      get() {
        if (typeof this.$store.state.main.video.result !== "undefined")
          return socialSharing;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    currentRouteName() {
      return this.$route.name;
    },
  },

  methods: {
    initializeUserData() {
      AppService.ensureLicenceLoaded()
        .then(() => {
          if (!this.$store.state.main.webshop_user.result) {
            return this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/me`,
              state: "webshop_user",
              host: "getWebshopHost",
            });
          }
        })
        .then(() => {
          if (!this.$store.state.main.user.result) {
            return this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/me`,
              state: "user",
            });
          }
        })
        .then(() => {
          const userSubject = this.$store.state.main.user.result?.subject;
          if (userSubject && this.$store.state.main.auth_user.result?.subject !== userSubject) {
            return this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/${userSubject}`,
              state: "auth_user",
              host: "getAuthHost",
            });

          }
        })
        .then(() => {
          const userResult = this.$store.state.main.user.result;
          if (userResult != undefined) {
            APIService.setUser(userResult);
            this.$vuetify.theme.dark = userResult.dark_theme;
          }
        })
        .then(() => {
          if (!this.$store.state.main.cart.result?.length) {
            this.$store.dispatch("fetch", { endpoint: `/api/v1/cart`, state: "cart" })
          }
        })
        .catch((err) => {
          this.$router.push("/install");
        });
    },

    setVuetifyPrimaryColor() {
      // Get primary color from Vuetify theme
      const primaryColor = this.$vuetify.theme.themes.light.primary.base || this.$vuetify.theme.themes.light.primary;
      // Set as CSS custom property on root element
      document.documentElement.style.setProperty('--vuetify-primary', primaryColor);
    },

    setCookie() {
      AppService.setCookie("cookieBanner", "true");
      this.cookieDialog = false;
    },

    imageSource(item, width, height) {
      return AppService.imageSource(item, width, height);
    },

    isTester() {
      return AppService.isTester();
    },

    isCreatorSubActive() {
      return AppService.isCreatorSubActive();
    },

    checkProduct(product) {
      return AppService.hasProduct(AppService.getProducts(product));
    },

    upgradePlan(name) {
      this.$store.dispatch("fetch", {
        endpoint: `/api/v1/products`,
        host: "getWebshopHost",
        state: "products",
      });
      this.$store.dispatch("fetch", {
        endpoint: `/api/v1/products/${this.$store.state.main.licence.result.product.crm_id}`,
        host: "getWebshopHost",
        state: "product",
      });
      this.$store.dispatch("modal/open", name);
    },

    logout() {
      this.$store.dispatch("auth/logout");
      this.linkTo('/')
    },

    currentUser(roles) {
      return userRole.hasRole(roles);
    },

    linkTo(route) {
      AppService.linkTo(route, this.$router);
    },

    imageSource(item) {
      let src = "./assets/logo/LogoMark_blue_new.png";
      if (item != null) {
        src = APIService.getApiHost() + `/${item}`;
      }
      return src;
    },

    openModal(name) {
      this.$store.dispatch("modal/open", name);
    },

    openCartDrawer(cartDrawerState) {
      this.$store.dispatch("fetch", {
        endpoint: `/api/v1/cart`,
        state: "cart",
      });
      // providers wird IMMER gefetcht: rightSiteMenue.vue mountet den Cart nur,
      // wenn state.main.providers.result definiert ist (Init: [] ohne .result).
      // Bei explicit (CCBill) entfaellt nur das Render: der statische
      // Template-Button in videoCart.vue uebernimmt den Checkout.
      this.$store
        .dispatch("fetch", {
          endpoint: `/api/v1/providers`,
          state: "providers",
        })
        .then((response) => {
          if (PaymentService.isExplicit(this.$store.state.main.config)) return;
          PaymentService.checkout(this, (response && response.result) || [], "EUR", ["reload", null]);
        })
        .catch((error) => {
          // Netzfehler: kein Render moeglich. Die Meldung beendet den
          // Skeleton-Loader des Cart.
          console.error("Cart: Provider-Fetch fehlgeschlagen", error);
          PaymentService.notifyPaypalMount("#paypal-checkout-container");
        });
      this.$store.commit("rightSiteDrawer/toogle", cartDrawerState);
    },

    // Item 18.3: Schuetteln des Warenkorb-Icons als Hinweis.
    // Ausgeloest per $root-Event aus den addToCart-Zweigen — auch wenn das
    // Produkt schon im Cart liegt (Kevin: bei JEDEM Versuch).
    shakeCart() {
      // Erst entfernen, dann im naechsten Tick setzen: sonst startet die
      // CSS-Animation bei zwei schnell aufeinanderfolgenden Adds nicht neu.
      this.cartShaking = false;
      this.$nextTick(() => {
        // Reflow erzwingen: ohne ihn fasst der Browser Entfernen und Setzen
        // der Klasse als eine Aenderung zusammen und die Animation laeuft
        // beim zweiten Mal nicht neu.
        const icon = this.$refs.cartIcon;
        if (icon) {
          void icon.offsetWidth;
        }
        this.cartShaking = true;
      });
    },

    openMobileMenu() {
      this.mobileDrawer = !this.mobileDrawer;
    },

    toogleDarkTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      this.$store.dispatch("patch", {
        endpoint: `/api/v1/users/${this.$store.state.main.user.result.id}`,
        event: {
          dark_theme: this.$vuetify.theme.dark,
        },
        message: false,
      });
    },

    isActiveLink(path) {
      const routePath = this.$route.path;
      const trimmedRoutePath = routePath.startsWith("/")
        ? routePath.slice(1)
        : routePath;
      const trimmedPath = path.startsWith("/") ? path.slice(1) : path;

      return (
        trimmedRoutePath === trimmedPath ||
        trimmedRoutePath.startsWith(trimmedPath + "/")
      );
    },

    getNavText(item) {
      const searchPath = item.url.startsWith('/') ? item.url : '/' + item.url;
      const route = this.$router.options.routes.find(r =>
        r.path && r.path.toLowerCase() === searchPath.toLowerCase()
      );
      if (route && route.meta && route.meta.label) {
        return this.$t(route.meta.label);
      }
      return this.$t(item.text);
    },
  },

  watch: {
    model(val) {
      if (val != null) this.tab = 0;
      else this.tab = null;
    },
    userImage() {
      // Neues Bild -> alten Fehlerzustand verwerfen, damit der zweite Versuch startet.
      this.avatarError = false;
    },
    "$store.state.auth.status.loggedIn"(newVal, oldVal) {

      if (newVal === true && oldVal === false) {
        this.initializeUserData();
      }
    },
    search(val) {
      // Items have already been loaded
      if (this.items.length > 0) return;

      this.isLoading = true;

      // Lazily load input items
      this.$store
        .dispatch("fetch", {
          endpoint: `/api/v1/search/?q=${val}`,
          state: "search",
          host: "getApiHost",
        })
        .then((res) => {
          this.items = res.result.categorie;
        })
        .catch((err) => {
          console.log("Error | Aus der Search auf App.Vue", err);
        })
        .finally(() => (this.isLoading = false));
    },
  },

  mounted() {
    // Set Vuetify primary color as CSS custom property for external CSS files
    this.setVuetifyPrimaryColor();

    // Eine offene PayPal-Subscription nachposten: die Freigabe passiert bei
    // PayPal, unser POST kann abgebrochen sein. Die Funktion prueft selbst, ob
    // ein Merker existiert und ob der Benutzer angemeldet ist.
    PaymentService.retryPendingSubscription(this);

    if (this.$store.state.auth.status.loggedIn) {
      this.initializeUserData();
    } else {
      this.$vuetify.theme.dark = true;
      this.$store.dispatch("fetch", {
        endpoint: "/api/v1/public",
        state: "config",
        header: false,
      })
        .then(() => {
          if (AppService.isExplicit()) {
            if (!AppService.getCookie("explicitConsent")) {
              this.$router.push("/explicitwarning");
            }
          }
        })
        .catch((error) => {
          console.log("error");
        });
      this.$store.dispatch("fetch", {
        endpoint: "/api/v1/shop/",
        state: "shop"
      });
    }

    // Check if the user has accepted the cookie banner
    if (!AppService.getCookie("cookieBanner")) {
      // Show the cookie banner
      this.cookieDialog = true;
    }
    // ####################

    // Item 18.3: addToCart meldet den Erfolg per $root-Event;
    // hier laeuft die Shake-Animation unabhaengig vom Aufrufer.
    this.$root.$on("cart:item-added", this.shakeCart);
  },

  beforeDestroy() {
    this.$root.$off("cart:item-added", this.shakeCart);
  },
};
</script>

<style lang="scss">
@font-face {
  font-family: "Open Sans";
  src: url("@/assets/fonts/Open_Sans/OpenSans-VariableFont_wdth,wght.ttf");
}

@font-face {
  font-family: "Space Grotesk";
  src: url("@/assets/fonts/Space_Grotesk/SpaceGrotesk-VariableFont_wght.ttf");
}

/* Der Footer der App: eine Linkzeile, darunter die Copyright-Zeile (Aufbau wie
   die Landingpage). Die Farben kommen aus dem Theme der Instanz. */
.app-footer {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 6px 12px;
  text-align: center;
}

.app-footer-links,
.app-footer-copy {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: center;
}

.app-footer-copy {
  margin-top: 12px;
}

.app-footer-sep {
  font-style: normal;
  opacity: 0.5;
}

// $font-family: "Space Grotesk";
$font-family: "Open Sans";

.v-application {
  // [class*="text-"] {
  //   font-family: $font-family, sans-serif !important;
  // }
  font-family: $font-family, sans-serif !important;
}

.fab,
.far,
.fas {
  color: #00c3ff;
}

/*Rules for sizing the icon*/
.v-icon.md-14 {
  font-size: 14px !important;
}

.v-icon.md-16 {
  font-size: 16px !important;
}

.v-icon.md-18 {
  font-size: 18px !important;
}

.v-icon.md-24 {
  font-size: 24px !important;
}

.v-icon.md-36 {
  font-size: 36px !important;
}

.v-icon.md-48 {
  font-size: 48px !important;
}

.v-icon.md-64 {
  font-size: 64px !important;
}

.brandColor {
  // color: #00c3ff;
  color: #00aeef;
}

.theme--dark.v-input,
.theme--dark.v-input input,
.theme--dark.v-input textarea {
  border-radius: 10px;
}

html {
  color-scheme: dark;
  background-color: #121212;
  /* dein App-Hintergrund */
}

/* Das Dokument traegt color-scheme dark. Der Browser malt dann hinter ein
   helles Fremd-iframe eine weisse Flaeche (PayPal-Button). Der Wert normal
   hebt diese Flaeche auf, damit der Button direkt auf der Karte sitzt. */
#paypalSubscriptionContainer iframe,
#paypal-checkout-container iframe,
#paypal-button-container iframe {
  color-scheme: normal;
}

/* NOTE: SELECTS ENTIRE SCROLLBAR ON ALL ELEMENTS (*) IT IS POSSIBLE TO STYLE JUST CERTAIN ELEMENTS */
*::-webkit-scrollbar {
  width: 6px;
  /* NOTE: THIS ONLY AFFECTS THE WIDTH OF VERTICAL SCROLL BARS */
  height: 6px;
  /* NOTE: THIS ONLY AFFECTS THE HEIGHT OF HORIZONTAL SCROLL BARS */
}

/* NOTE: THIS IS THE TRACK THAT THE POSITIONING TAG SLIDES ON */
*::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.04);
  // margin-block: 5px;
  // border-radius: 100vmax;                    /* NOTE: THIS WILL ROUND THE EDGES, USE 100VMAX TO TOTALLY ROUND THE CORNERS OR A FEW PIXELS TO MAKE IT MORE SQUARISH */
}

*::-webkit-scrollbar-thumb {
  // A quiet thumb in the dark tone of the app.
  background-color: rgba(255, 255, 255, 0.14);
  border-radius: 3px;
}

/* NOTE: THIS WILL AFFECT THE SCROLLBAR UPON HOVER BY THE MOUSE.
   The primary color comes from setVuetifyPrimaryColor(). */
*::-webkit-scrollbar-thumb:hover {
  background-color: var(--vuetify-primary, #00c3ff);
}

@supports not selector(::-webkit-scrollbar) {
  * {
    scrollbar-color: rgba(255, 255, 255, 0.14) transparent;
    scrollbar-width: thin;
  }
}

/* Der Haupt-Scrollbalken liegt als klassischer Balken NEBEN dem Inhalt und nimmt ihm
   6 bis 17 px weg (Breite je nach Browser und Betriebssystem). Der App-Bar ist
   `position: fixed` mit left/right 0 und nimmt die Layout-Breite (innerWidth), der
   Inhalt endet aber an clientWidth: der Balken stand rechts um die Gutter-Breite
   ueber. Deshalb belegt der Balken des html keinen Platz mehr; dann sind innerWidth
   und clientWidth gleich und der Balken sitzt buendig wie der Inhalt.
   Die Balken in den eigenen Scroll-Bereichen (Galerie, Playlists) behalten die 6 px
   aus der Regel oben. */
html {
  scrollbar-width: none;
}

html::-webkit-scrollbar {
  width: 0;
  height: 0;
}

// absolute Top left 11
.absoluteTL11 {
  position: absolute;
  top: 11px;
  left: 11px;
  z-index: 9;
}

// absolute Top right 11
.absoluteTR11 {
  position: absolute;
  top: 11px;
  right: 11px;
  z-index: 9;
}

// absolute Top right 11
.absoluteBL11 {
  position: absolute;
  bottom: 11px;
  left: 11px;
  z-index: 9;
}

// absolute Top right 11
.absoluteBR11 {
  position: absolute;
  bottom: 11px;
  right: 11px;
  z-index: 9;
}

// // Scrollbar für Firefox
// .scroller {
//   overflow-y: scroll;
//   scrollbar-color: #00c3ff #C2D2E4;
//   scrollbar-width: thin;
// }

// absolute Top left 22
.absoluteTL22 {
  position: absolute;
  top: 22px;
  left: 22px;
  z-index: 9;
}

// absolute Top right 22
.absoluteTR22 {
  position: absolute;
  top: 22px;
  right: 22px;
  z-index: 9;
}

// absolute Top right 22
.absoluteBL22 {
  position: absolute;
  bottom: 22px;
  left: 22px;
  z-index: 9;
}

// absolute Top right 22
.absoluteBR22 {
  position: absolute;
  bottom: 22px;
  right: 22px;
  z-index: 9;
}

nav li:hover,
nav li.router-link-active,
nav li.router-link-exact-active {
  background-color: indianred;
  cursor: pointer;
}

.vhl-list {
  overflow-x: hidden !important;
}

// absolute Top left 22
.absoluteTL33 {
  position: absolute;
  top: 33px;
  left: 33px;
  z-index: 9;
}

// absolute Top right 33
.absoluteTR33 {
  position: absolute;
  top: 33px;
  right: 33px;
  z-index: 9;
}

// absolute Top right 33
.absoluteBL33 {
  position: absolute;
  bottom: 33px;
  left: 33px;
  z-index: 9;
}

// absolute Top right 33
.absoluteBR33 {
  position: absolute;
  bottom: 33px;
  right: 33px;
  z-index: 9;
}

.click_cursor {
  cursor: pointer;
}

.zoom-in_cursor {
  cursor: zoom-in;
}

.zoom-out_cursor {
  cursor: zoom-out;
}

.btnNoHover:before {
  display: none;
}

video::-webkit-media-controls-enclosure {
  display: none !important;
}

.absoluteBottom {
  position: absolute;
  bottom: 15px;
  left: 15px;
}

// .v-input--selection-controls .v-input__slot > .v-label, .v-input--selection-controls .v-radio > .v-label {
//   left: 8px !important;
// }

// Wird das überhaupt erkannt?
:not(:root):fullscreen .video-controls {
  z-index: 99999999999;
  position: absolute;
  padding-left: 50px;
}

.animated-gradient-dBlue-red-radial {
  background: rgb(0, 0, 0);
  /* background: -moz-radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,31,255,1) 100%);
  background: -webkit-radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,31,255,1) 100%);
  background: radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,31,255,1) 100%);
  filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#000000",endColorstr="#001fff",GradientType=1); */
  background: linear-gradient(132deg,
      rgb(30, 144, 255) 0%,
      rgb(138, 43, 226) 100%);
  z-index: 0;
  background-size: 400% 400%;
  -webkit-animation: animated-gradient-darkBlue-black 21s ease infinite;
  -moz-animation: animated-gradient-darkBlue-black 21s ease infinite;
  animation: animated-gradient-darkBlue-black 10s ease infinite;
}

@-webkit-keyframes animated-gradient-darkBlue-black {
  0% {
    background-position: 100% 0%;
  }

  50% {
    background-position: 0% 100%;
  }

  100% {
    background-position: 100% 0%;
  }
}

@-moz-keyframes animated-gradient-darkBlue-black {
  0% {
    background-position: 100% 0%;
  }

  50% {
    background-position: 0% 100%;
  }

  100% {
    background-position: 100% 0%;
  }
}

@keyframes animated-gradient-darkBlue-black {
  0% {
    background-position: 100% 0%;
  }

  50% {
    background-position: 0% 100%;
  }

  100% {
    background-position: 100% 0%;
  }
}

@keyframes cart-shake {
  0% {
    transform: translateX(0) scale(1);
  }

  12% {
    transform: translateX(-4px) scale(1.12);
  }

  28% {
    transform: translateX(4px) scale(1.12);
  }

  44% {
    transform: translateX(-3px) scale(1.12);
  }

  60% {
    transform: translateX(3px) scale(1.12);
  }

  80% {
    transform: translateX(-1px) scale(1.06);
  }

  100% {
    transform: translateX(0) scale(1);
  }
}

.cart-shake {
  animation: cart-shake 0.75s ease-out;
}
</style>
